import { Suspense, useState, type ReactNode } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  getListInterviewInviteSessionsQueryOptions,
  getGetInterviewInviteSessionTurnsBySessionQueryOptions,
  type InterviewInviteAttemptDto,
  type InterviewInviteAttemptDtoStatus,
} from '@sara/api-client';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@sara/ui/components/tabs';
import { Skeleton } from '@sara/ui/components/skeleton';
import { TooltipProvider } from '@sara/ui/components/tooltip';
import { cn } from '@sara/ui/lib/utils';
import { CalendarClock, Clock, MessagesSquare, ClipboardCheck, Phone } from 'lucide-react';
import { toSuspense } from '../../../../lib/query';
import { AttemptStatusBadge } from '../../components/AttemptStatusBadge';
import { ChannelTag, EndedByTag } from '../../components/CallAttempts';
import { formatUsPhoneInput } from '../../components/PhoneInput';
import { BriefResults } from '../../components/InterviewBriefDetail/BriefResults';
import { ScoresSection } from '../../components/InterviewBriefDetail/ScoresSection';
import {
  formatDate,
  formatDateTime,
  formatDurationSecs,
} from '../../components/InterviewBriefDetail/format';
import type { InterviewAttemptsContainerProps } from './types';

const CONSUMED: InterviewInviteAttemptDtoStatus[] = ['FINISHED', 'PROCESSING', 'DONE', 'FAILED'];
const COMPLETED: InterviewInviteAttemptDtoStatus[] = ['FINISHED', 'PROCESSING', 'DONE'];

export function InterviewAttemptsContainerContent({
  inviteId,
  renderTurn,
}: InterviewAttemptsContainerProps) {
  const { data: attempts } = useSuspenseQuery(
    toSuspense(getListInterviewInviteSessionsQueryOptions(inviteId))
  );
  // Default to the completed attempt (the one that produced a result), falling
  // back to any consumed attempt, then the latest.
  const primary =
    attempts.find((a) => a.result) ??
    attempts.find((a) => CONSUMED.includes(a.status)) ??
    attempts.at(-1);
  const [selected, setSelected] = useState(primary?.id);

  if (attempts.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/80 bg-card/40 px-6 py-12 text-center">
        <p className="text-sm italic text-muted-foreground">No call attempts yet.</p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tabs value={selected} onValueChange={setSelected} className="space-y-4">
        {attempts.length > 1 && (
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-muted-foreground">
              <span className="text-[10.5px] font-medium uppercase tracking-[0.1em]">
                {attempts.length} attempts
              </span>
            </div>
            <TabsList className="h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
              {attempts.map((a) => (
                <TabsTrigger
                  key={a.id}
                  value={a.id}
                  className={cn(
                    'h-auto flex-col items-start gap-1.5 rounded-xl border border-border bg-card px-3.5 py-2.5 shadow-none',
                    'data-[state=active]:border-primary/40 data-[state=active]:bg-primary/5 data-[state=active]:shadow-none',
                    'transition-colors hover:border-border hover:bg-secondary/50'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-foreground">
                      Attempt {a.attemptNumber}
                    </span>
                    <AttemptStatusBadge status={a.status} />
                  </span>
                  <span className="text-[10.5px] font-normal text-muted-foreground/80">
                    {formatDate(a.callStartedAt ?? a.createdAt)}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
        )}

        {attempts.map((a) => (
          <TabsContent key={a.id} value={a.id} className="mt-0">
            <AttemptDetail
              inviteId={inviteId}
              attempt={a}
              renderTurn={renderTurn}
              singleAttempt={attempts.length === 1}
            />
          </TabsContent>
        ))}
      </Tabs>
    </TooltipProvider>
  );
}

/** Per-attempt metric strip: start, duration, who ended, turn count. */
function AttemptPanel({
  attempt,
  singleAttempt,
}: {
  attempt: InterviewInviteAttemptDto;
  singleAttempt: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <h2 className="text-[15px] font-semibold text-foreground">
            Attempt {attempt.attemptNumber}
          </h2>
          <ChannelTag viaPhone={attempt.viaPhone} />
          {singleAttempt && <AttemptStatusBadge status={attempt.status} />}
        </div>
      </div>
      <dl className="mt-3.5 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        {attempt.viaPhone && attempt.dialedPhoneNumber && (
          <Stat
            icon={<Phone className="h-3.5 w-3.5" strokeWidth={1.6} />}
            label="Called"
            value={
              <span className="font-mono text-[13px] text-foreground">
                {formatUsPhoneInput(attempt.dialedPhoneNumber)}
              </span>
            }
          />
        )}
        <Stat
          icon={<CalendarClock className="h-3.5 w-3.5" strokeWidth={1.6} />}
          label="Started"
          value={
            <span className="font-mono text-[13px] text-foreground">
              {formatDateTime(attempt.callStartedAt)}
            </span>
          }
        />
        <Stat
          icon={<Clock className="h-3.5 w-3.5" strokeWidth={1.6} />}
          label="Duration"
          value={
            <span className="font-mono text-[13px] font-semibold tabular-nums text-foreground">
              {formatDurationSecs(attempt.callDurationSecs)}
            </span>
          }
        />
        <Stat
          icon={<span className="text-[10px] font-bold leading-none">!</span>}
          label="Ended by"
          value={
            attempt.endedBy ? (
              <span className="text-[13px] text-foreground">
                <EndedByTag endedBy={attempt.endedBy} />
              </span>
            ) : (
              <span className="text-[13px] text-muted-foreground">—</span>
            )
          }
        />
        <Stat
          icon={<MessagesSquare className="h-3.5 w-3.5" strokeWidth={1.6} />}
          label="Turns"
          value={
            <span className="font-mono text-[13px] tabular-nums text-foreground">
              {attempt.turnCount}
            </span>
          }
        />
      </dl>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-muted-foreground">
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-md bg-secondary text-muted-foreground">
          {icon}
        </span>
        <span className="text-[10px] font-medium uppercase tracking-[0.1em]">{label}</span>
      </dt>
      <dd className="mt-1.5 min-w-0 truncate">{value}</dd>
    </div>
  );
}

type AttemptView = 'results' | 'transcript';

/**
 * The selected attempt's body: a compact metric strip plus a Results/Transcript
 * tab set (kept as tabs rather than stacked so the panel stays short). Defaults
 * to Results when the attempt produced one, otherwise straight to the transcript.
 */
function AttemptDetail({
  inviteId,
  attempt,
  renderTurn,
  singleAttempt,
}: {
  inviteId: string;
  attempt: InterviewInviteAttemptDto;
  renderTurn: InterviewAttemptsContainerProps['renderTurn'];
  singleAttempt: boolean;
}) {
  const hasResults = Boolean(attempt.result || attempt.rubricScore);
  const [view, setView] = useState<AttemptView>(hasResults ? 'results' : 'transcript');

  return (
    <div className="space-y-4">
      <AttemptPanel attempt={attempt} singleAttempt={singleAttempt} />

      <Tabs value={view} onValueChange={(v) => setView(v as AttemptView)} className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList>
            <TabsTrigger value="results" className="gap-1.5">
              <ClipboardCheck className="h-3.5 w-3.5" strokeWidth={1.6} />
              Results
            </TabsTrigger>
            <TabsTrigger value="transcript" className="gap-1.5">
              <MessagesSquare className="h-3.5 w-3.5" strokeWidth={1.6} />
              Transcript
            </TabsTrigger>
          </TabsList>
          {view === 'results' && attempt.result && (
            <span className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground/80">
              Processed {formatDate(attempt.result.processedAt)}
            </span>
          )}
        </div>

        <TabsContent value="results" className="mt-0">
          {attempt.result || attempt.rubricScore ? (
            <div className="space-y-6">
              {attempt.rubricScore && <ScoresSection score={attempt.rubricScore} />}
              {attempt.result && <BriefResults result={attempt.result} />}
            </div>
          ) : (
            <ResultsEmptyState status={attempt.status} />
          )}
        </TabsContent>

        <TabsContent value="transcript" className="mt-0">
          {/* Per-tab boundary so opening a transcript for the first time shows a
              spinner here only — not collapsing the surrounding panel. */}
          <Suspense
            fallback={
              <div className="space-y-3">
                <Skeleton className="h-16 w-3/4 rounded-lg" />
                <Skeleton className="ml-auto h-16 w-3/4 rounded-lg" />
              </div>
            }
          >
            <AttemptTurns
              inviteId={inviteId}
              sessionId={attempt.id}
              callStartedAt={attempt.callStartedAt}
              renderTurn={renderTurn}
            />
          </Suspense>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ResultsEmptyState({ status }: { status: InterviewInviteAttemptDtoStatus }) {
  // Completed-but-not-yet-scored vs. a session that can never produce results.
  const pending = COMPLETED.includes(status);
  return (
    <div className="rounded-xl border border-dashed border-border/80 bg-card/40 px-5 py-8 text-center">
      <p className="text-sm font-medium text-foreground">
        {pending ? 'Results are being generated.' : 'No results for this attempt.'}
      </p>
      <p className="mx-auto mt-1.5 max-w-sm text-[13px] text-muted-foreground">
        {pending
          ? 'This session finished and its results are still processing — check back shortly.'
          : 'Results are only generated for completed sessions.'}
      </p>
    </div>
  );
}

function AttemptTurns({
  inviteId,
  sessionId,
  callStartedAt,
  renderTurn,
}: {
  inviteId: string;
  sessionId: string;
  callStartedAt: string | null;
  renderTurn: InterviewAttemptsContainerProps['renderTurn'];
}) {
  const { data: turns } = useSuspenseQuery(
    toSuspense(getGetInterviewInviteSessionTurnsBySessionQueryOptions(inviteId, sessionId))
  );
  if (turns.length === 0) {
    return <p className="text-sm italic text-muted-foreground">No transcript available.</p>;
  }
  return (
    <div className="rounded-xl border border-border bg-card divide-y divide-border">
      {turns.map((turn) => (
        <div key={turn.id} className="px-5">
          {renderTurn(turn, callStartedAt)}
        </div>
      ))}
    </div>
  );
}
